import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { 
  Plus, Edit2, Trash2, Upload, Star, TrendingUp, 
  Award, DollarSign, Calendar, Image as ImageIcon, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const EnhancedTireManagement = () => {
  const [tires, setTires] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTire, setEditingTire] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  const [formData, setFormData] = useState({
    size: '',
    category: '',
    brand: '',
    price: '',
    stock_status: 'In Stock',
    quantity_available: '100',
    photo_url: '',
    treadwear_rating: '',
    star_rating: '',
    discount_type: 'none',
    discount_value: '',
    sale_start_date: '',
    sale_end_date: '',
    is_featured: false,
    is_best_seller: false,
    member_only_discount: ''
  });

  useEffect(() => {
    fetchTires();
  }, []);

  const fetchTires = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tires`);
      setTires(response.data || []);
    } catch (error) {
      console.error('Error fetching tires:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return null;

    const formData = new FormData();
    formData.append('file', imageFile);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/tires/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.image_url;
    } catch (error) {
      throw new Error('Image upload failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let photoUrl = formData.photo_url;
      
      // Upload new image if selected
      if (imageFile) {
        photoUrl = await uploadImage();
      }

      const tireData = {
        ...formData,
        price: parseFloat(formData.price),
        quantity_available: parseInt(formData.quantity_available),
        treadwear_rating: formData.treadwear_rating ? parseInt(formData.treadwear_rating) : null,
        star_rating: formData.star_rating ? parseFloat(formData.star_rating) : null,
        discount_value: formData.discount_value ? parseFloat(formData.discount_value) : null,
        member_only_discount: formData.member_only_discount ? parseFloat(formData.member_only_discount) : null,
        photo_url: photoUrl,
        sale_start_date: formData.sale_start_date || null,
        sale_end_date: formData.sale_end_date || null
      };

      if (editingTire) {
        // URL encode the tire size to handle slashes
        const encodedSize = encodeURIComponent(editingTire.size);
        await axios.put(`${BACKEND_URL}/api/admin/tires/${encodedSize}`, tireData);
        toast.success('Tire updated successfully!');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/tires`, tireData);
        toast.success('Tire added successfully!');
      }

      resetForm();
      fetchTires();
    } catch (error) {
      toast.error('Operation failed', {
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (tire) => {
    setEditingTire(tire);
    setFormData({
      size: tire.size || '',
      category: tire.category || '',
      brand: tire.brand || '',
      price: tire.price?.toString() || '',
      stock_status: tire.stock_status || 'In Stock',
      quantity_available: tire.quantity_available?.toString() || '100',
      photo_url: tire.photo_url || '',
      treadwear_rating: tire.treadwear_rating?.toString() || '',
      star_rating: tire.star_rating?.toString() || '',
      discount_type: tire.discount_type || 'none',
      discount_value: tire.discount_value?.toString() || '',
      sale_start_date: tire.sale_start_date ? tire.sale_start_date.split('T')[0] : '',
      sale_end_date: tire.sale_end_date ? tire.sale_end_date.split('T')[0] : '',
      is_featured: tire.is_featured || false,
      is_best_seller: tire.is_best_seller || false,
      member_only_discount: tire.member_only_discount?.toString() || ''
    });
    setImagePreview(tire.photo_url ? `${BACKEND_URL}${tire.photo_url}` : null);
    setShowForm(true);
  };

  const handleDelete = async (size) => {
    if (!window.confirm('Are you sure you want to delete this tire?')) return;

    try {
      // URL encode the tire size to handle slashes
      const encodedSize = encodeURIComponent(size);
      await axios.delete(`${BACKEND_URL}/api/admin/tires/${encodedSize}`);
      toast.success('Tire deleted successfully!');
      fetchTires();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingTire(null);
    setImageFile(null);
    setImagePreview(null);
    setFormData({
      size: '',
      category: '',
      brand: '',
      price: '',
      stock_status: 'In Stock',
      quantity_available: '100',
      photo_url: '',
      treadwear_rating: '',
      star_rating: '',
      discount_type: 'none',
      discount_value: '',
      sale_start_date: '',
      sale_end_date: '',
      is_featured: false,
      is_best_seller: false,
      member_only_discount: ''
    });
  };

  const calculateFinalPrice = () => {
    const basePrice = parseFloat(formData.price) || 0;
    const discountType = formData.discount_type;
    const discountValue = parseFloat(formData.discount_value) || 0;

    if (discountType === 'percentage') {
      return basePrice - (basePrice * discountValue / 100);
    } else if (discountType === 'fixed') {
      return basePrice - discountValue;
    }
    return basePrice;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Tire Management</h2>
          <p className="text-sm text-gray-600 mt-1">{tires.length} total tires</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchTires} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowForm(!showForm)} className="bg-red-600 hover:bg-red-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Tire
          </Button>
        </div>
      </div>

      {showForm && (
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            {editingTire ? 'Edit Tire' : 'Add New Tire'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Basic Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tire Size *</Label>
                  <Input
                    name="size"
                    value={formData.size}
                    onChange={handleInputChange}
                    required
                    placeholder="225/65R17"
                  />
                </div>
                <div>
                  <Label>Brand *</Label>
                  <Input
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    required
                    placeholder="Michelin"
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select Category</option>
                    <option value="All-Season">All-Season</option>
                    <option value="Performance">Performance</option>
                    <option value="Winter">Winter</option>
                    <option value="Truck/SUV">Truck/SUV</option>
                  </select>
                </div>
                <div>
                  <Label>Price ($) *</Label>
                  <Input
                    name="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    placeholder="129.99"
                  />
                </div>
                <div>
                  <Label>Stock Status</Label>
                  <select
                    name="stock_status"
                    value={formData.stock_status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="In Stock">In Stock</option>
                    <option value="Low Stock">Low Stock</option>
                    <option value="Out of Stock">Out of Stock</option>
                  </select>
                </div>
                <div>
                  <Label>Quantity</Label>
                  <Input
                    name="quantity_available"
                    type="number"
                    value={formData.quantity_available}
                    onChange={handleInputChange}
                    placeholder="100"
                  />
                </div>
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Tire Image</h4>
              <div className="flex items-center gap-4">
                <label className="cursor-pointer">
                  <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300">
                    <Upload className="w-4 h-4" />
                    <span className="text-sm">Choose Image</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </label>
                {imagePreview && (
                  <div className="relative w-24 h-24 border rounded-md overflow-hidden">
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </div>

            {/* Ratings */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Ratings & Reviews</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Treadwear Rating</Label>
                  <Input
                    name="treadwear_rating"
                    type="number"
                    value={formData.treadwear_rating}
                    onChange={handleInputChange}
                    placeholder="500"
                  />
                </div>
                <div>
                  <Label>Star Rating (0-5)</Label>
                  <Input
                    name="star_rating"
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={formData.star_rating}
                    onChange={handleInputChange}
                    placeholder="4.5"
                  />
                </div>
              </div>
            </div>

            {/* Discounts */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Pricing & Discounts</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Discount Type</Label>
                  <select
                    name="discount_type"
                    value={formData.discount_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="none">No Discount</option>
                    <option value="percentage">Percentage Off</option>
                    <option value="fixed">Fixed Amount Off</option>
                  </select>
                </div>
                {formData.discount_type !== 'none' && (
                  <div>
                    <Label>
                      {formData.discount_type === 'percentage' ? 'Discount (%)' : 'Discount ($)'}
                    </Label>
                    <Input
                      name="discount_value"
                      type="number"
                      step="0.01"
                      value={formData.discount_value}
                      onChange={handleInputChange}
                      placeholder={formData.discount_type === 'percentage' ? '10' : '20.00'}
                    />
                  </div>
                )}
                <div>
                  <Label>Member-Only Discount (%)</Label>
                  <Input
                    name="member_only_discount"
                    type="number"
                    step="0.01"
                    value={formData.member_only_discount}
                    onChange={handleInputChange}
                    placeholder="5"
                  />
                </div>
              </div>
              
              {formData.price && formData.discount_type !== 'none' && formData.discount_value && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-800">
                    <strong>Final Price:</strong> ${calculateFinalPrice().toFixed(2)}
                    <span className="ml-2 line-through text-gray-500">${formData.price}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Sale Dates */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Sale Period (Optional)</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sale Start Date</Label>
                  <Input
                    name="sale_start_date"
                    type="date"
                    value={formData.sale_start_date}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <Label>Sale End Date</Label>
                  <Input
                    name="sale_end_date"
                    type="date"
                    value={formData.sale_end_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* Feature Flags */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Badges & Features</h4>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleInputChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Featured</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_best_seller"
                    checked={formData.is_best_seller}
                    onChange={handleInputChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Best Seller</span>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button type="submit" disabled={isLoading} className="bg-red-600 hover:bg-red-700">
                {isLoading ? 'Saving...' : editingTire ? 'Update Tire' : 'Add Tire'}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Tires List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tires.map((tire) => (
          <Card key={tire.size} className="p-4">
            {tire.photo_url && (
              <div className="w-full h-40 bg-gray-100 rounded-md mb-3 overflow-hidden">
                <img 
                  src={`${BACKEND_URL}${tire.photo_url}`} 
                  alt={tire.size}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            <div className="flex justify-between items-start mb-2">
              <div>
                <h4 className="font-bold text-gray-900">{tire.size}</h4>
                <p className="text-sm text-gray-600">{tire.brand}</p>
              </div>
              <Badge className="bg-blue-100 text-blue-800">{tire.category}</Badge>
            </div>

            <div className="space-y-2 mb-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Price:</span>
                <span className="font-semibold text-green-600">${tire.price}</span>
              </div>
              
              {tire.star_rating && (
                <div className="flex items-center gap-1 text-sm">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span className="font-medium">{tire.star_rating}</span>
                </div>
              )}
              
              <div className="flex gap-2 flex-wrap">
                {tire.is_featured && (
                  <Badge className="bg-purple-100 text-purple-800 text-xs">
                    <Award className="w-3 h-3 mr-1" />
                    Featured
                  </Badge>
                )}
                {tire.is_best_seller && (
                  <Badge className="bg-orange-100 text-orange-800 text-xs">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    Best Seller
                  </Badge>
                )}
                {tire.discount_value && (
                  <Badge className="bg-red-100 text-red-800 text-xs">
                    <DollarSign className="w-3 h-3 mr-1" />
                    Sale
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-3 border-t">
              <Button size="sm" variant="outline" onClick={() => handleEdit(tire)} className="flex-1">
                <Edit2 className="w-3 h-3 mr-1" />
                Edit
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => handleDelete(tire.size)}
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
